/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import React, { ReactElement, useEffect, useState } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { Module, Node, Button } from '@msdyn365-commerce-modules/utilities';
import ProcessBar from './components/process-bar';
import SoldTos from './components/soldtos';
import ShipTos from './components/shiptos';
import { BackButton } from './components/back-button';
import { OrderHeaderInfo } from './components/order-header-info';
import Products from './components/products';
import Overview from './components/overview';
import Acknowledgement from './components/acknowledgement';
import Alert from '../../components/alert';
import MessageView from './components/message-view';
import { useModal } from '../../components/modal';
import {
    CREATE_ORDER_COLUMN_INCOTERM,
    CREATE_ORDER_COLUMN_SALES_ORG,
    CREATE_ORDER_COLUMN_BUSINESS_UNIT,
    CREATE_ORDER_COLUMN_SALES_ORG_TEXT,
    CREATE_ORDER_COLUMN_DIVISION_TEXT,
    CREATE_ORDER_COLUMN_DISTRIBUTION_CHANNEL
} from '../../configuration';
import { ILxsCreateOrderResources } from './lxs-create-order.props.autogenerated';
import useStepNavigation from './components/stepNavigation';
import { IHeader, ILxsCreateOrderViewProps } from './lxs-create-order.data';

function SwitchComponents({ active, children, resources }: { active: any; children: ReactElement[]; resources: ILxsCreateOrderResources }) {
    const activeItems = children.filter(child => child.key === active.toString());
    return activeItems && activeItems.length ? (
        activeItems[0]
    ) : (
        <p>
            {resources.createOrderNoChildWithKey} &ldquo;{active}&ldquo;
        </p>
    );
}

export const CreateOrderSoldTosHeader: React.FC<IHeader> = ({ headerProps, heading, orderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);

const CreateOrderView: React.FC<ILxsCreateOrderViewProps> = ({
    moduleProps,
    header,
    pageSize,
    resources,
    context,
    soldToData,
    shipToData,
    productsData,
    saveCartError,
    confirmedOrderData,
    productsHaveEndcustomers,
    fetchPrices,
    setSoldTo,
    setShipTo,
    cartData,
    processStarted,
    loadSoldTos,
    setProducts,
    setCartData,
    placeOrder,
    orderData,
    step,
    setStep,
    hasNoReorderItems,
    setOrderHeader
}) => {
    const tomorrow = new Date(+new Date() + 86400000);

    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [isColumnFilterValid, setIsColumnFilterValid] = useState<boolean>(true);
    const [filterDisabled, setFilterDisabled] = useState<boolean>(false);
    const [showAddressInformation, setShowAddressInformation] = useState<boolean>(true);
    const [resetAddressFlyout, setResetAddressFlyout] = useState<boolean>(false);
    const [globalDate, setGlobalDate] = useState<Date>(new Date(tomorrow));

    const { orderNumber = '' } = context.actionContext.requestContext.query ?? {};
    const { setOpen, setClose } = useModal();

    useEffect(() => {
        if (orderData.products && orderData.products.length === 0 && filterDisabled) {
            setFilterDisabled(false);
            setColumnFilters([]);
        } else if (orderData.products && orderData.products.length > 0 && !filterDisabled && isColumnFilterValid) {
            setColumnFilters([
                { id: CREATE_ORDER_COLUMN_SALES_ORG, value: orderData.orderHeader?.SalesOrg },
                { id: CREATE_ORDER_COLUMN_BUSINESS_UNIT, value: orderData.orderHeader?.Division },
                { id: CREATE_ORDER_COLUMN_INCOTERM, value: orderData.orderHeader?.Incoterm1 },
                { id: CREATE_ORDER_COLUMN_SALES_ORG_TEXT, value: orderData.orderHeader?.SalesOrgText },
                { id: CREATE_ORDER_COLUMN_DIVISION_TEXT, value: orderData.orderHeader?.DivisionText },
                { id: CREATE_ORDER_COLUMN_DISTRIBUTION_CHANNEL, value: orderData.orderHeader?.DistributionChannel }
            ]);
            setFilterDisabled(true);
        }
    }, [orderData]);

    useEffect(() => {
        if (step > 2) {
            setOrderHeader({
                ...orderData.orderHeader,
                SalesOrg: (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_SALES_ORG)?.value as string) ?? '',
                SalesOrgText: (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_SALES_ORG_TEXT)?.value as string) ?? '',
                Division: (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_BUSINESS_UNIT)?.value as string) ?? '',
                DivisionText: (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_DIVISION_TEXT)?.value as string) ?? '',
                Incoterm1: (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_INCOTERM)?.value as string) ?? '',
                DistributionChannel:
                    (columnFilters.find(filter => filter.id === CREATE_ORDER_COLUMN_DISTRIBUTION_CHANNEL)?.value as string) ?? ''
            });
        }
    }, [columnFilters]);

    useEffect(() => {
        if (!isColumnFilterValid) {
            setColumnFilters([]);
        }
    }, [isColumnFilterValid]);

    const pushHistoryState = (currentStep: number) => {
        /**
         * workaround for chrome browsers. chrome keeps skipping pages because these pages are recognized as no change pages.
         * topic: history manipulation intervention
         */
        const initialStep = 0;
        window.history.pushState({ initialStep }, '', `#step-${initialStep}`);
        for (let i = 1; i < currentStep + 1; i++) {
            window.history.pushState({ i }, '', `#step-${i}`);
        }
    };

    // hide flyout after 800ms on initial page load
    useEffect(() => {
        setShowAddressInformation(true);
    }, [resetAddressFlyout]);

    /**
     * custom hook to keep track of browser-history and url hash
     */
    useStepNavigation(
        step,
        resources,
        context,
        orderData,
        setGlobalFilter,
        setOpen,
        setClose,
        setStep,
        setSoldTo,
        setShipTo,
        pushHistoryState
    );

    return (
        <Module {...moduleProps}>
            {context.actionContext.requestContext.user.isAuthenticated ? (
                <>
                    <ProcessBar
                        step={step}
                        setStep={setStep}
                        products={orderData.products ?? []}
                        setSoldTo={setSoldTo}
                        setShipTo={setShipTo}
                        resources={resources}
                        pushHistoryState={pushHistoryState}
                    />

                    {saveCartError && (
                        <div className='row'>
                            <div className='col-12'>
                                <Alert type='danger' message={resources.createOrderErrorSaveCart} iconSize={1} />
                            </div>
                        </div>
                    )}
                    <div className='row'>
                        <div className='col-12'>
                            <SwitchComponents active={step} resources={resources}>
                                {header && <CreateOrderSoldTosHeader {...header} />}
                                <MessageView
                                    cartData={cartData}
                                    loadSoldTos={loadSoldTos}
                                    processStarted={processStarted}
                                    resources={resources}
                                    setCartData={setCartData}
                                    isReordering={!!orderNumber}
                                    hasNoReorderItems={hasNoReorderItems}
                                    setResetAddressFlyout={setResetAddressFlyout}
                                    key='0'
                                />
                                <SoldTos
                                    filterDisabled={filterDisabled}
                                    isColumnFilterValid={isColumnFilterValid}
                                    processStarted={processStarted}
                                    resources={resources}
                                    globalFilter={globalFilter}
                                    setGlobalFilter={setGlobalFilter}
                                    columnFilters={columnFilters}
                                    setColumnFilters={setColumnFilters}
                                    soldToData={soldToData}
                                    pageSize={pageSize}
                                    orderData={orderData}
                                    setSoldTo={setSoldTo}
                                    loadSoldTos={loadSoldTos}
                                    showAddressInformation={showAddressInformation}
                                    key='1'
                                ></SoldTos>
                                <ShipTos
                                    filterDisabled={filterDisabled}
                                    isColumnFilterValid={isColumnFilterValid}
                                    setIsColumnFilterValid={setIsColumnFilterValid}
                                    resources={resources}
                                    globalFilter={globalFilter}
                                    setGlobalFilter={setGlobalFilter}
                                    columnFilters={columnFilters}
                                    setColumnFilters={setColumnFilters}
                                    pageSize={pageSize}
                                    orderData={orderData}
                                    setShipTo={setShipTo}
                                    shipToData={shipToData}
                                    showAddressInformation={showAddressInformation}
                                    key='2'
                                />
                                <div key='3'>
                                    <Products
                                        filterDisabled={filterDisabled}
                                        setFilterDisabled={setFilterDisabled}
                                        isColumnFilterValid={isColumnFilterValid}
                                        setIsColumnFilterValid={setIsColumnFilterValid}
                                        productsData={productsData}
                                        columnFilters={columnFilters}
                                        setColumnFilters={setColumnFilters}
                                        setProducts={setProducts}
                                        globalFilter={globalFilter}
                                        productsHaveEndcustomers={productsHaveEndcustomers}
                                        setGlobalFilter={setGlobalFilter}
                                        pageSize={pageSize}
                                        orderData={orderData}
                                        resources={resources}
                                        setStep={setStep}
                                        setShipTo={setShipTo}
                                        step={step}
                                        pushHistoryState={pushHistoryState}
                                        showAddressInformation={showAddressInformation}
                                        globalDate={globalDate}
                                        setGlobalDate={setGlobalDate}
                                        fetchPrices={fetchPrices}
                                    ></Products>
                                </div>
                                <div key='4'>
                                    <OrderHeaderInfo
                                        orderData={orderData}
                                        context={context}
                                        setOrderHeader={setOrderHeader}
                                        setStep={setStep}
                                        resources={resources}
                                        productsHaveEndcustomers={productsHaveEndcustomers}
                                        step={step}
                                        pushHistoryState={pushHistoryState}
                                    ></OrderHeaderInfo>
                                </div>
                                <div key='5'>
                                    <Overview
                                        placeOrder={placeOrder}
                                        context={context}
                                        orderData={orderData}
                                        resources={resources}
                                        step={step}
                                        productsHaveEndcustomers={productsHaveEndcustomers}
                                        pushHistoryState={pushHistoryState}
                                    ></Overview>
                                </div>
                                <div key='6'>
                                    <Acknowledgement
                                        context={context}
                                        orderData={confirmedOrderData}
                                        resources={resources}
                                        productsHaveEndcustomers={productsHaveEndcustomers}
                                    ></Acknowledgement>
                                </div>
                            </SwitchComponents>
                            <div className='lxs-create-order__back-button'>
                                <BackButton
                                    step={step}
                                    setStep={setStep}
                                    setSoldTo={setSoldTo}
                                    resources={resources}
                                    pushHistoryState={pushHistoryState}
                                />
                            </div>
                        </div>
                        {/* only hide address information on first three components in wizard (soldTo, shopTo, products) */}
                        {step > 0 && step <= 3 && (
                            <Button
                                title={resources.createOrderAddressInformation}
                                className={`lxs-create-order__show-address-btn ${
                                    showAddressInformation
                                        ? 'lxs-create-order__show-address-btn--open'
                                        : 'lxs-create-order__show-address-btn--closed'
                                }`}
                                onClick={() => setShowAddressInformation(!showAddressInformation)}
                            ></Button>
                        )}
                    </div>
                </>
            ) : (
                <div className='msc-alert__container'>
                    <Alert message={resources.createOrderNotLoggedIn} type='info' iconSize={1} />
                    {
                        <p
                            className='msc-alert__explanation'
                            dangerouslySetInnerHTML={{ __html: resources.createOrderNotLoggedInExplanation }}
                        />
                    }
                    <a className='msc-cta__primary' href={context.request.user.signInUrl}>
                        {resources.createOrderLogIn}
                    </a>
                </div>
            )}
        </Module>
    );
};

export default CreateOrderView;
